// HomePage.jsx
// LandingShell renders the hero; this file renders feature sections below.
// Each section includes an image slot (drop in a URL or component later).

import * as React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import MapIcon from '@mui/icons-material/Map';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import OilBarrelIcon from '@mui/icons-material/OilBarrel';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';

import imgMap3d from "../assets/images/map3d.png";
import imgFpz from "../assets/images/fpz.png";
import imgOffsets from "../assets/images/offsets.png";
import imgReport from "../assets/images/report.png";

function FeatureSection({
  id,
  eyebrow,
  title,
  description,
  bullets,
  imageAlt = "",
  imageUrl,
  reverse = false,
}) {
  return (
    <Box id={id} sx={{ py: { xs: 5, md: 7 } }}>
      <Container maxWidth="lg" disableGutters>
        <Grid
          container
          spacing={{ xs: 3, md: 4 }}
          direction={reverse ? "row-reverse" : "row"}
          alignItems="center"
        >
          {/* Copy */}
          <Grid item xs={12} md={6}>
            <Stack spacing={2}>
              {eyebrow && (
                <Typography
                  variant="overline"
                  sx={{ letterSpacing: 1.2, opacity: 0.9 }}
                >
                  {eyebrow}
                </Typography>
              )}

              <Typography variant="h4" fontWeight={900} sx={{ letterSpacing: -0.5 }}>
                {title}
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560 }}>
                {description}
              </Typography>

              {bullets?.length ? (
                <Stack spacing={1.25} sx={{ pt: 1 }}>
                  {bullets.map((b, idx) => (
                    <Box
                      key={`${idx}-${b}`}
                      sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: 10,
                          bgcolor: "primary.main",
                          mt: "7px",
                          flex: "0 0 auto",
                        }}
                      />
                      <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
                        {b}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              ) : null}
            </Stack>
          </Grid>

          {/* Image slot */}
          <Grid item xs={12} md={6}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                height: { xs: 260, sm: 320, md: 380 },
              }}
            >
              {imageUrl ? (
                <Box
                  component="img"
                  src={imageUrl}
                  alt={imageAlt}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "grid",
                    placeItems: "center",
                    bgcolor: "action.hover",
                    border: "1px dashed",
                    borderColor: "divider",
                  }}
                >
                  <Stack spacing={0.5} sx={{ textAlign: "center", px: 3 }}>
                    <Typography variant="subtitle2" fontWeight={800}>
                      Image slot
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Provide imageUrl to replace this placeholder
                    </Typography>
                  </Stack>
                </Box>
              )}
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

FeatureSection.propTypes = {
  id: PropTypes.string,
  eyebrow: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  bullets: PropTypes.arrayOf(PropTypes.string),
  imageAlt: PropTypes.string,
  imageUrl: PropTypes.string,
  reverse: PropTypes.bool,
};

function FeatureGrid({ items }) {
  return (
    <Box sx={{ py: { xs: 5, md: 7 } }}>
      <Container maxWidth="lg">
        {/*
        <Typography variant="h5" fontWeight={900} sx={{ mb: 1 }}>
          Core capabilities
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 760 }}>
          From visualization and well selection to FPZ definition, offset-well analysis, and report delivery.
        </Typography>
        */}

        <Grid container spacing={2}>
          {items.map((it) => (
            <Grid key={it.title} item xs={12} sm={6} md={3}>
              <Card variant="outlined" sx={{ borderRadius: 3, height: "100%" }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography fontWeight={900} sx={{ mb: 0.75 }}>
                    {it.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {it.body}
                  </Typography>

                  <Box
                    sx={{
                      borderRadius: 2,
                      overflow: "hidden",
                      height: 120,
                      border: "1px dashed",
                      borderColor: "divider",
                      bgcolor: "action.hover",
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {it?.icon && React.cloneElement(it.icon, { sx: { fontSize: 40, color: 'primary.main' } })}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

FeatureGrid.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      body: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default function HomePage({
  images = {
    visualize: imgMap3d,
    fpz: imgFpz,
    offsets: imgOffsets,
    reports: imgReport,
  },
}) {
  const quickTiles = [
    { title: "Map", body: "Browse wells on a map and select treatment wells.", icon: <MapIcon /> },
    { title: "3D", body: "View well placement and spacing with 3D rendering.", icon: <ViewInArIcon />  },
    { title: "Wells + Surveys", body: "Review directional surveys and trajectories.", icon: <OilBarrelIcon />  },
    { title: "Search", body: "Find wells fast by API/permit/operator/attributes.", icon:<TravelExploreIcon />  },
  ];

  return (
    <Box>
      <FeatureGrid items={quickTiles} />

      <Divider />

      <FeatureSection
        id="visualize"
        eyebrow="Visualize"
        title="Map and 3D visualization with well and directional surveys"
        description="Work within a geospatial app to find the wells and view details. Navigate the map, view well data, and visualize placement in 3D."
        bullets={[
          "Map-first workflow to orient the area before analysis",
          "3D context to understand spacing, vertical separation, and proximity",
          "Directional surveys and well geometry from surface location to bottom hole",
          "Selection tools that help build job plans",
        ]}
        imageUrl={images.visualize}
        imageAlt="Visualization: map and 3D well context"
      />

      <Divider />

      <FeatureSection
        id="fpz"
        reverse
        eyebrow="FPZ"
        title="Define fracture planning zones across multiple treatment wells"
        description="Treat FPZ setup as a repeatable step. Select one or many treatment wells, then apply radius rules that match your planning needs."
        bullets={[
          "Select multiple treatment wells in a single workflow",
          "Set multiple FPZ radii (per plan, per stage grouping, or scenario)",
          "Carry selections forward into analysis and reporting",
        ]}
        imageUrl={images.fpz}
        imageAlt="FPZ setup: multiple treatment wells and radius definitions"
      />

      <Divider />

      <FeatureSection
        id="offsets"
        eyebrow="Offset wells"
        title="Automatic offset selection inside FPZ with risk-oriented analysis"
        description="Offset wells are selected automatically within the FPZ so the analysis stays consistent. The system focuses attention where it matters by identifying proximity and exposure risk patterns."
        bullets={[
          "Automatically selects wells inside the fracture planning zones",
          "Eliminates manual hunting for offsets and reduces missed candidates",
          "Analyzes proximity-based risk drivers and highlights outliers",
          "Keeps the selection method consistent across reports and teams",
        ]}
        imageUrl={images.offsets}
        imageAlt="Offset selection and risk analysis"
      />

      <Divider />

      <FeatureSection
        id="reports"
        reverse
        eyebrow="Reports"
        title="Create, save, edit, and share reports with folder organization"
        description="Reporting is built into the workflow. Create reports directly from the selected wells and analysis, then store and manage outputs as a library rather than isolated files."
        bullets={[
          "Create reports seamlessly from the analysis state",
          "Save reports for quick retrieval and reuse",
          "Edit reports without rebuilding selections from scratch",
          "Share reports across collaborators and teams",
          "Organize reports in folders to match projects and clients",
        ]}
        imageUrl={images.reports}
        imageAlt="Report creation, management, and sharing"
      />

      <Box sx={{ height: 24 }} />
    </Box>
  );
}

HomePage.propTypes = {
  images: PropTypes.shape({
    visualize: PropTypes.string,
    fpz: PropTypes.string,
    offsets: PropTypes.string,
    reports: PropTypes.string,
  }),
};
